import { useQueryClient } from "@tanstack/react-query"
import { ArrowLeft } from "lucide-react"
import { Suspense, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import { roomApi, roomParams } from "@/api/client/room"
import { getPropertyGetQueryKey, getRoomGetQueryKey } from "@/api/generated/endpoints/api"
import { getErrorMessage } from "@/api/instance"
import { RoomForm, type RoomFormValues } from "@/components/room"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Spinner } from "@/components/ui/spinner/Spinner"

export function RoomEditPage() {
  const navigate = useNavigate()
  const { propertyId, roomId } = useParams<{ propertyId: string; roomId: string }>()

  if (!propertyId) {
    return <div>物件IDが指定されていません</div>
  }

  if (!roomId) {
    return <div>部屋IDが指定されていません</div>
  }

  return (
    <ReactErrorBoundary>
      <Suspense fallback={<RoomEditPageFallback />}>
        <RoomEditPageContent propertyId={propertyId} roomId={roomId} navigate={navigate} />
      </Suspense>
    </ReactErrorBoundary>
  )
}

interface RoomEditPageContentProps {
  propertyId: string
  roomId: string
  navigate: ReturnType<typeof useNavigate>
}

function RoomEditPageContent({ propertyId, roomId, navigate }: RoomEditPageContentProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()

  // APIからデータをフェッチ
  const { data } = roomApi.useRoomGetSuspense(roomId, {
    withProperty: roomParams.RoomGetWithProperty.true,
  })

  const room = data.room

  // roomUpdateのmutationを取得
  const { mutateAsync: updateRoom } = roomApi.useRoomUpdate({
    mutation: {
      onError: (error) =>
        toast.error("部屋の更新に失敗しました", { description: getErrorMessage(error) }),
    },
  })

  const handleSubmit = async (values: RoomFormValues) => {
    setIsSubmitting(true)
    try {
      await updateRoom({
        id: roomId,
        data: {
          propertyId: Number(propertyId),
          name: values.name,
          layout: values.layout,
          // 面積・階数は任意。未入力は API が受け付ける 0 で送る（オーナー画面と同じ）
          size: values.size ?? 0,
          floor: values.floor ?? 0,
        },
      })
      // キャッシュを無効化して再フェッチ
      await queryClient.invalidateQueries({ queryKey: getRoomGetQueryKey(roomId) })
      await queryClient.invalidateQueries({ queryKey: getPropertyGetQueryKey(propertyId) })
      toast.success("部屋を更新しました")
      navigate(`/properties/${propertyId}`)
    } catch {
      // 失敗の表示は mutation の onError が担当する
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate(`/properties/${propertyId}`)
  }

  return (
    <div>
      {/* ヘッダーナビゲーション */}
      <div className="flex items-center gap-2 p-6">
        <button
          type="button"
          onClick={() => navigate(`/properties/${propertyId}`)}
          className="flex items-center justify-center w-7 h-7 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]"
        >
          <ArrowLeft size={14} className="text-[#354267]" />
        </button>
        <span className="text-sm font-semibold text-[#3f4652]">物件詳細に戻る</span>
      </div>

      <div className="max-w-[895px] mx-auto px-6 pb-8">
        <RoomForm
          defaultValues={{
            name: room.name,
            layout: room.layout,
            size: String(room.size),
            floor: String(room.floor),
          }}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
          isEdit
        />
      </div>
    </div>
  )
}

function RoomEditPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className="text-slate-600 size-10 transition-opacity" />
    </div>
  )
}
